import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import styles from './Playlists.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { X, Play } from 'lucide-react';
import { deletePlaylist, getUserPlaylists } from '../../api/playListAPI';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Helmet } from 'react-helmet';

const PlaylistCard = ({ item, onRemove, isMobile }) => {
  const navigate = useNavigate();

 
const handleNavigation = () => {
  if (item.item_type === "video") {
    navigate(`/video/${item.contentDetails.slug}`);
  } else if (item.item_type === "ebook" && item.contentDetails.series_id === null) {
    navigate(`/book-detail/${item.contentDetails.slug}`);
  } else if (item.item_type === "ebook" && item.contentDetails.series_id !== null) {
    navigate(`/book-details/${item.contentDetails.slug}`);
  } else {
    navigate(`/audio-play/${item.contentDetails.slug}`);
  }
};

  const handleImageError = (e) => {
    e.target.src = "/api/placeholder/400/240";
  };

  const getImageUrl = () => {
    if (item.item_type === 'video' && item.contentDetails) {
      return item.contentDetails.mobile_image_url || 
             item.contentDetails.computer_image_url || 
             "/api/placeholder/400/240";
    }
    if (item.item_type === 'ebook' && item.contentDetails) {
      return item.contentDetails.cover_image_url_for_mbl || 
             item.contentDetails.cover_image_url_for_computer || 
             "/api/placeholder/400/240";
    }
    if (item.item_type === 'audio' && item.contentDetails) {
      return item.contentDetails.mobile_image_url || 
             item.contentDetails.computer_image_url || 
             "/api/placeholder/400/240";
    }
    return "/api/placeholder/400/240";
  };

  const getTitle = () => {
    if (item.contentDetails && item.contentDetails.title) {
      return item.contentDetails.title;
    }
    return item.title || "Untitled";
  };

  return (
    <div className={`${styles.card} ${isMobile ? styles.mobileCard : ''}`} onClick={handleNavigation}>
      <div className={styles.imageContainer}>
        <img
          src={getImageUrl()}
          alt={getTitle()}
          className={`${styles.cardImage} ${styles[item.item_type]}`}
          onError={handleImageError}
        />
        {!isMobile && (item.item_type === "shorts" || item.item_type === "video" || item.item_type === "audio") && (
          <div className={styles.playButton}>
            <Play />
          </div>
        )}
        <button 
          className={`${styles.removeButton} ${isMobile ? styles.alwaysShow : ''}`} 
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          aria-label="Remove from playlist"
        >
          <X />
        </button>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className={styles.card}>
    <Skeleton height={180} className={styles.cardImage} />
    <div className={styles.cardTitle}>
      <Skeleton count={2} />
    </div>
  </div>
);

const Playlists = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limitPerPage = 20;
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [slidesPerView, setSlidesPerView] = useState(getSlidesPerView());

  function getSlidesPerView() {
    const width = window.innerWidth;
    if (width <= 480) return 3;
    if (width <= 828) return 5;
    if (width <= 1024) return 7;
    if (width <= 2490) return 7;
    return 5;
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setSlidesPerView(getSlidesPerView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchUserPlaylists = async () => {
      try {
        setLoading(true);
        const data = await getUserPlaylists(page, limitPerPage);
        if (!data.success) throw new Error('Failed to fetch playlists');
        setPlaylists(page === 1 ? data.playlists || [] : prev => [...prev, ...(data.playlists || [])]);
        if (data.pagination) {
          setTotalPages(data.pagination.pages || 1);
          setHasMore(page < (data.pagination.pages || 1));
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load playlists. Please try again later.');
        setLoading(false);
        toast.error('Failed to load playlists');
      }
    };
    fetchUserPlaylists();
  }, [page]);

  const handleRemove = async (id) => {
    try {
      const response = await deletePlaylist(id);
      if (response.success) {
        setPlaylists(playlists.filter(item => item.id !== id));
        toast.info("Removed from your playlist", {
          position: "top-right",
          style: { color: "black" },
          progressStyle: { background: "green" },
        });
      } else {
        throw new Error('Failed to remove item');
      }
    } catch (err) {
      toast.error("Failed to remove from playlist");
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const uniqueTypes = [...new Set(playlists.map(item => item.item_type))];
  const groupedByType = uniqueTypes.map(type => ({
    type,
    items: playlists.filter(item => item.item_type === type),
  }));

  if (error && playlists.length === 0) return <div className={styles.error}>{error}</div>;
  if (playlists.length === 0 && !loading) return <div className={styles.empty}>Your playlist is empty</div>;

  const renderSkeletonSlides = () => {
    return Array(slidesPerView * 2).fill().map((_, index) => (
      <SwiperSlide key={`skeleton-${index}`}>
        <SkeletonCard />
      </SwiperSlide>
    ));
  };

  return (
    <>
    <Helmet>
      <title>My Playlist - SunoKahaniyan</title>
      <meta name="description" content="View and manage your playlists on SunoKahaniyan." />
    </Helmet>
    <div className={styles.container}>
      <h1 className={styles.title}>My Playlist</h1>
      {groupedByType.map(({ type, items }) => (
        <div key={type} className={styles.typeSection}>
          <h2 className={styles.typeTitle}>{type.charAt(0).toUpperCase() + type.slice(1)}s</h2>
          
          <Swiper
            slidesPerView={slidesPerView}
            spaceBetween={20}
            navigation={true}
            modules={[Navigation, Mousewheel]}
            mousewheel={{ forceToAxis: true, invert: false, sensitivity: 1 }}
            className={styles.swiper}
          >
            {loading && playlists.length === 0 ? 
              renderSkeletonSlides() :
              items.map(item => (
                <SwiperSlide key={item.id}>
                  <PlaylistCard item={item} onRemove={handleRemove} isMobile={isMobile} />
                </SwiperSlide>
              ))
            }
          </Swiper>
        </div>
      ))}
      
      {hasMore && (
        <button onClick={loadMore} className={styles.loadMoreBtn} disabled={loading}>
          {loading ? 'Loading...' : 'Load More'}
        </button>
      )}
      <ToastContainer />
    </div>
    </>
  );
};

export default Playlists;